import { getAuthToken } from './auth';
import config from './config';

const API_BASE_URL = `${config.API_BASE_URL}/api`;

export interface UserCredits {
  credits: number | string;  // number or "unlimited"
  credits_used: number;
  is_unlimited: boolean;
}

export interface CreditHistory {
  timestamp: string;
  operation: string;
  cost: number;
  credits_before: number;
  credits_after: number;
  details?: Record<string, unknown>;
}

export interface CreditsResponse {
  success: boolean;
  credits?: UserCredits | number | string;  // Can be nested object or flat values
  credits_used?: number;
  is_unlimited?: boolean;
  history?: CreditHistory[];
  message?: string;
}

/**
 * Fetch user's current credits
 */
export async function getUserCredits(): Promise<UserCredits | null> {
  const token = getAuthToken();

  if (!token) {
    // Return null instead of throwing - caller will handle this
    return null;
  }

  const response = await fetch(`${API_BASE_URL}/credits`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch credits' }));
    throw new Error(error.message || 'Failed to fetch credits');
  }

  const data: CreditsResponse = await response.json();

  if (!data.success) {
    throw new Error(data.message || 'Invalid credits response');
  }

  // Handle both formats:
  // 1) Nested: { success, credits: { credits, credits_used, is_unlimited } }
  // 2) Flat: { success, credits, credits_used, is_unlimited }
  if (typeof data.credits === 'object' && data.credits !== null) {
    return data.credits as UserCredits;
  }

  // Flat format - construct UserCredits object
  return {
    credits: data.credits as number | string,
    credits_used: data.credits_used ?? 0,
    is_unlimited: data.is_unlimited ?? false
  };
}

/**
 * Fetch user's credit usage history
 */
export async function getCreditHistory(): Promise<CreditHistory[]> {
  const token = getAuthToken();

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/credits/history`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch history' }));
    throw new Error(error.message || 'Failed to fetch history');
  }

  const data: CreditsResponse = await response.json();

  if (!data.success || !data.history) {
    throw new Error(data.message || 'Invalid history response');
  }

  return data.history;
}
